import React from 'react';
import DOMPurify from 'dompurify';
import LessonActivityCard, { activityThemes } from '../../../Components/LessonActivityCard';
import TopicsSection from './TopicsSection';
import grammarExplanations from '../../../Mainpages/Grammar-Exercises/components/grammarExplanations';
import lessonGrammarQuiz from '../../../Mainpages/Grammar-Exercises/components/lessonGrammarQuiz';

const TopicsGrammarExplanation = ({ data }) => {
  // Referenced explanation: render the shared component for this
  // grammar point from the Grammar-Exercises folder. The panel
  // header already shows languageExplanationTitle, and
  // grammar-page.css hides the component's own h2 inside the panel.
  if (data.grammarPoint) {
    const Explanation = grammarExplanations[data.grammarPoint];
    if (!Explanation) {
      return (
        <p className="grammar-explanation-missing">
          No explanation has been written for the grammar point &quot;{data.grammarPoint}&quot; yet.
        </p>
      );
    }
    return <Explanation />;
  }

  // Inline explanation (sections not yet migrated to a reference):
  // HTML from text[], DOMPurify-sanitised, tables pulled out and
  // re-interleaved after each paragraph.
  return (
    <div className="grammar-explanation-json">
      {data.languageExplanationTitle && <h2>{data.languageExplanationTitle}</h2>}
      {(() => {
      // Join all text elements and split by table tags to reconstruct properly
      const fullHtml = data.text.join('');
      // Match table elements with their content
      const tableRegex = /<table>[\s\S]*?<\/table>/g;
      const tables = fullHtml.match(tableRegex) || [];

      // Remove tables from the main content
      let remainingHtml = fullHtml.replace(tableRegex, '<<TABLE_PLACEHOLDER>>');
      const paragraphs = remainingHtml.split('<<TABLE_PLACEHOLDER>>');

      const result = [];
      paragraphs.forEach((para, i) => {
        if (para.trim()) {
          result.push(<div key={`p-${i}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(para) }} />);
        }
        if (tables[i]) {
          result.push(<div key={`table-${i}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tables[i]) }} />);
        }
      });
      return result.map((elem, idx) => {
        // Check if it's a dangerouslySetInnerHTML element
        if (elem.props?.dangerouslySetInnerHTML) {
          const sanitizedHtml = DOMPurify.sanitize(elem.props.dangerouslySetInnerHTML.__html);
          return (
            <div key={elem.key} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
          );
        }
        return elem;
      });
    })()}
  </div>
  );
};

// Grammar quiz sections reference a shared quiz file by
// grammar point. The file's questions are passed through
// whole so the topic dropdown still offers the full pool;
// TopicsSection starts on the lesson's own questions
// (the section's `topic` field holds the lesson name).
const resolveQuizSection = (sectionData) => {
  if (sectionData.type !== 'grammarquiz' || !sectionData.grammarPoint) {
    return sectionData;
  }
  const quizData = lessonGrammarQuiz[sectionData.grammarPoint];
  if (!quizData) return sectionData;
  return { ...quizData, ...sectionData };
};

const TopicsRouter = ({ sectionData }) => {
  if (!sectionData) return null;

  const { type } = sectionData;

  const renderContent = () => {
    switch (type) {
      case "grammarexplanation":
        return <TopicsGrammarExplanation data={sectionData} />;
      case "quiz":
      case "grammarquiz":
      case "triviaquiz":
      case "categorization":
        // The lesson panel header already shows the section
        // title, so the activity renders without its own heading.
        return (
          <TopicsSection
            type={type}
            data={resolveQuizSection(sectionData)}
            hideTitle
          />
        );
      default:
        return <TopicsSection type={type} data={sectionData} />;
    }
  };

  return (
    <LessonActivityCard type={type} plain>
      {renderContent()}
    </LessonActivityCard>
  );
};

export { activityThemes };
export default TopicsRouter;
